/**
 * Button — eDMS Design System
 *
 * Source: drafts/31_ux_design_system_spec.md §3.1
 * Owner: Claude 3 (Frontend/UX)
 *
 * Accessibility:
 * - Min 44×44 px touch target on mobile (Apple HIG / WCAG 2.5.5 AAA).
 * - Visible :focus-visible ring.
 * - aria-busy during loading; aria-disabled with reason via tooltip.
 * - Loading state preserves button width (no layout shift).
 *
 * Variants:
 * - primary (one per view; blue): main action.
 * - secondary (outlined): less prominent.
 * - tertiary (text-only): link-style.
 * - destructive (red): reject, cancel, withdraw.
 * - success (green): rarely used.
 * - ai-advisory (violet): AI-suggested actions.
 */

import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { clsx } from 'clsx';
import styles from './Button.module.css';

export type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'destructive'
  | 'success'
  | 'ai-advisory';

export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Show a spinner replacing the button text. Width is preserved. */
  isLoading?: boolean;
  /** Visually full width within container. */
  isFullWidth?: boolean;
  /** Icon shown before the label (for icon-only, use `aria-label`). */
  leadingIcon?: ReactNode;
  /** Icon shown after the label. */
  trailingIcon?: ReactNode;
  children?: ReactNode;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      isLoading = false,
      isFullWidth = false,
      leadingIcon,
      trailingIcon,
      disabled,
      className,
      children,
      type = 'button',
      ...rest
    },
    ref
  ) => {
    const isInteractionDisabled = disabled || isLoading;
    return (
      <button
        ref={ref}
        type={type}
        disabled={isInteractionDisabled}
        aria-busy={isLoading || undefined}
        className={clsx(
          styles.button,
          styles[variant],
          styles[size],
          isFullWidth && styles.fullWidth,
          isLoading && styles.loading,
          className
        )}
        {...rest}
      >
        {isLoading && <span className={styles.spinner} aria-hidden="true" />}
        <span className={clsx(styles.content, isLoading && styles.contentHidden)}>
          {leadingIcon && (
            <span className={styles.leadingIcon} aria-hidden="true">
              {leadingIcon}
            </span>
          )}
          {children}
          {trailingIcon && (
            <span className={styles.trailingIcon} aria-hidden="true">
              {trailingIcon}
            </span>
          )}
        </span>
      </button>
    );
  }
);

Button.displayName = 'Button';
